import React from "react";

import { FormProvider, useForm } from "react-hook-form";
import { QueryClient, useQueryClient } from "@tanstack/react-query";

import { Button, Input, Modal, setSnack, useManualModalState } from "@synthesis/shared-ui";
import { useUpdate4, findByProjectIdQueryKey } from "@synthesis/shared-services/models/modelsDrawingsController/hooks";

import { useStores } from "../../../store";

type AddModalRenameFolderProps = {
  id: string;
  type: string;
  name: string;
};

type ModalReNameProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  name: string;
  openModal: () => void;
  closeModal: () => void;
} & AddModalRenameFolderProps;

type FormFields = {
  name: string;
};

const ModalReName = React.memo((props: ModalReNameProps) => {
  const { type, id, name, ...root } = props;
  return (
    <Modal.Root manual {...root}>
      <AddModalRenameFolder type={type} id={id} name={name} />
    </Modal.Root>
  );
});

const AddModalRenameFolder = React.memo(({ type, id, name }: AddModalRenameFolderProps) => {
  const queryClient: QueryClient = useQueryClient();
  const methods = useForm<FormFields>({ defaultValues: { name } });
  const { directoryStore, fileStore, generalStore } = useStores();
  const { closeModal } = useManualModalState();
  const updateDrawing = useUpdate4();

  const onSubmit = async (data: FormFields) => {
    const nextName = data.name.trim();

    if (!nextName) {
      return setSnack({
        title: "Введите наименование",
        messageType: "error",
      });
    }

    if (name === nextName) {
      return setSnack({
        title: `${type === "DIRECTORY" ? "Папка" : type === "DRAWING" ? "Чертёж" : "Файл"} с таким именем уже существует.`,
        messageType: "error",
      });
    }

    if (type === "DIRECTORY") {
      await directoryStore.renameDirectory({ name: nextName }, id, queryClient);
      closeModal();
      return;
    }

    if (type === "MODEL" || type === "DOCUMENT" || type === "PDF_DOCUMENT") {
      await fileStore.renameFile({ name: nextName }, id);
      closeModal();
      return;
    }

    if (type === "DRAWING") {
      const projectId = generalStore.projectId;

      try {
        await updateDrawing.mutateAsync({
          body: { drawingId: id, name: nextName },
        });

        if (projectId) {
          await queryClient.invalidateQueries({ queryKey: findByProjectIdQueryKey({ path: { projectId } }) });
        }

        setSnack({
          title: `Чертёж «${nextName}» успешно переименован`,
          messageType: "info",
        });
        closeModal();
      } catch (error) {
        const debugMessage = (error as { response?: { data?: { debugMessage?: unknown } } } | null)?.response?.data?.debugMessage;
        setSnack({
          title:
            typeof debugMessage === "string" && debugMessage.includes("already exists")
              ? "Файл с таким названием уже существует"
              : "Не удалось переименовать чертёж",
          messageType: "error",
        });
      }
    }
  };

  return (
    <Modal.Content className="w-[35vw] min-w-[350px]">
      <Modal.Title>Переименовать</Modal.Title>
      <FormProvider {...methods}>
        <form className="mt-2 px-5" onSubmit={methods.handleSubmit(onSubmit)}>
          <Input data-cy="modalReName-input-projectDocks" type="text" name="name" placeholder="Введите новое наименование" />
          <Button data-cy="modalReName-button-projectDocks" className="my-2" isFullWidth type="submit">
            Подтвердить
          </Button>
        </form>
      </FormProvider>
    </Modal.Content>
  );
});

export default ModalReName;
